import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import createShippingProfile from '@/shared/api/shipping/profiles/createShippingProfile';
import updateMultipleProfiles from '@/shared/api/shipping/profiles/updateMultipleProfiles';
import createZone from '@/shared/api/shipping/zones/createZone';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';

import ShippingProfileRequestBody from '../types/createShippingProfileRequestBody.type';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Args = {
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
};

const useShippingProfilesOperations = ({
  onUpdateDialogState,
  onUpdateNotificationBar,
  onUpdateProgressDialog,
}: Args) => {
  const { t } = useTranslation(['shipping']);
  const redirectLogin = useRedirectLogin();

  const [refreshTableIndex, setRefreshTableIndex] = useState(0);

  const handleZoneCreate = async (
    profileId: string,
    zone: ShippingProfileRequestBody['zone'],
  ) => {
    onUpdateProgressDialog({
      title: t('shipping:creatingZone'),
    });

    const response = await createZone({
      profileId,
      body: zone,
      onUnauthorized: redirectLogin,
    });

    onUpdateProgressDialog({ open: false });

    if (response.success) {
      onUpdateDialogState(DialogName.CreateEditShippingProfileDialog, {
        open: false,
      });
      onUpdateNotificationBar({
        message: t('shipping:shippingProfileCreated'),
        open: true,
      });
    } else {
      onUpdateDialogState(DialogName.CreateEditShippingProfileDialog, {
        errors: [response.msg],
      });
    }
  };

  const handleProfileCreate = async (value: ShippingProfileRequestBody) => {
    onUpdateProgressDialog({
      title: t('shipping:creatingShippingProfile'),
      open: true,
    });

    const response = await createShippingProfile({
      body: value.profile,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      setRefreshTableIndex((prev) => prev + 1);
      await handleZoneCreate(response.data[0].shipping_profile_id, value.zone);
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.CreateEditShippingProfileDialog, {
        errors: [response.msg],
      });
    }
  };

  const handleAssignProductsAndVariants = async (
    profiles: ShippingProfile[],
    productVariants: ShippingProfile['product_variants'],
  ) => {
    onUpdateProgressDialog({
      title: t('shipping:assigningProductsAndVariants'),
      open: true,
      progress: 0,
    });

    const profileIds = profiles.map((profile) => profile.shipping_profile_id);

    const response = await updateMultipleProfiles({
      profileIds,
      body: {
        product_variants: productVariants,
      },
      onUnauthorized: redirectLogin,
      onProgress: (progress) => onUpdateProgressDialog({ progress }),
    });

    onUpdateProgressDialog({ open: false });

    if (response.every((res) => res.success)) {
      onUpdateNotificationBar({
        message: t('shipping:productsAndVariantsAssignedSuccess'),
        open: true,
      });
      onUpdateDialogState(DialogName.CreateEditShippingProfileDialog, {
        open: false,
      });
    } else {
      onUpdateDialogState(DialogName.CreateEditShippingProfileDialog, {
        errors: response.filter((res) => !res.success).map((res) => res.msg),
      });
    }
  };

  return {
    refreshTableIndex,
    handleProfileCreate,
    handleAssignProductsAndVariants,
  };
};

export default useShippingProfilesOperations;
